/**
 * CSV parsing utilities for user import
 */
import { UserImportRow, CSV_IMPORT_COLUMNS } from '@mygate/common';

export interface ParsedCSVResult {
  headers: string[];
  rows: UserImportRow[];
  errors: ParseError[];
  rawData: string[][];
}

export interface ParseError {
  row: number;
  message: string;
}

/**
 * Parse CSV text into rows
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Push last field
  result.push(current.trim());
  return result;
};

/**
 * Parse CSV text content
 */
export const parseCSV = (content: string): string[][] => {
  const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');
  return lines.map(parseCSVLine);
};

/**
 * Map CSV data to UserImportRow objects
 */
export const mapCSVToUsers = (rawData: string[][], walletKeyDelimiter: string = '|'): ParsedCSVResult => {
  if (rawData.length < 2) {
    return {
      headers: rawData[0] || [],
      rows: [],
      errors: [{ row: 0, message: 'CSV must contain header row and at least one data row' }],
      rawData,
    };
  }

  const headers = rawData[0];
  const errors: ParseError[] = [];
  const rows: UserImportRow[] = [];

  // Create header index map for efficient lookups
  const headerIndex: Record<string, number> = {};
  headers.forEach((header, index) => {
    headerIndex[header.trim()] = index;
  });

  // Validate required columns exist
  const requiredColumns = [
    CSV_IMPORT_COLUMNS.FIRSTNAME,
    CSV_IMPORT_COLUMNS.LASTNAME,
    CSV_IMPORT_COLUMNS.EMAIL,
    CSV_IMPORT_COLUMNS.MOBILE,
  ];

  const missingColumns = requiredColumns.filter(col => headerIndex[col] === undefined);
  if (missingColumns.length > 0) {
    return {
      headers,
      rows: [],
      errors: [{ row: 0, message: `Missing required columns: ${missingColumns.join(', ')}` }],
      rawData,
    };
  }

  // Process data rows
  for (let i = 1; i < rawData.length; i++) {
    const row = rawData[i];

    // Skip empty rows
    if (row.every(cell => cell.trim() === '')) {
      continue;
    }

    const getValue = (colName: string): string => {
      const index = headerIndex[colName];
      return index !== undefined ? (row[index] || '').trim() : '';
    };

    // Parse wallet keys with delimiter
    const walletKeysRaw = getValue(CSV_IMPORT_COLUMNS.WALLET_KEYS);
    const tanganyWallets = walletKeysRaw
      ? walletKeysRaw
          .split(walletKeyDelimiter)
          .map(k => k.trim())
          .filter(Boolean)
      : [];

    // Combine street and street number for addressLine1
    const street = getValue(CSV_IMPORT_COLUMNS.STREET);
    const streetNumber = getValue(CSV_IMPORT_COLUMNS.STREET_NUMBER);
    const addressLine1 = street && streetNumber ? `${street} ${streetNumber}` : street || streetNumber;

    // Use Alpha2 for country code
    const country = getValue(CSV_IMPORT_COLUMNS.COUNTRY_ALPHA2);

    const userRow: UserImportRow = {
      firstName: getValue(CSV_IMPORT_COLUMNS.FIRSTNAME),
      lastName: getValue(CSV_IMPORT_COLUMNS.LASTNAME),
      email: getValue(CSV_IMPORT_COLUMNS.EMAIL),
      preferredLanguage: getValue(CSV_IMPORT_COLUMNS.LANGUAGE) || 'de',
      tanganyWallets,
      addressLine1,
      zipCode: getValue(CSV_IMPORT_COLUMNS.ZIP),
      city: getValue(CSV_IMPORT_COLUMNS.CITY),
      country,
      birthDate: getValue(CSV_IMPORT_COLUMNS.BIRTH_DATE),
      phoneNumber: getValue(CSV_IMPORT_COLUMNS.MOBILE),
    };

    rows.push(userRow);
  }

  return {
    headers,
    rows,
    errors,
    rawData,
  };
};

/**
 * Validate a single user row
 */
export const validateUserRow = (row: UserImportRow): string[] => {
  const errors: string[] = [];

  if (!row.firstName?.trim()) {
    errors.push('First name is required');
  }

  if (!row.lastName?.trim()) {
    errors.push('Last name is required');
  }

  if (!row.email?.trim()) {
    errors.push('Email is required');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row.email)) {
      errors.push('Invalid email format');
    }
  }

  if (!row.phoneNumber?.trim()) {
    errors.push('Phone number is required');
  } else {
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    if (!phoneRegex.test(row.phoneNumber)) {
      errors.push('Invalid phone format (must be E.164, e.g., +491234567890)');
    }
  }

  if (row.birthDate && row.birthDate.trim() !== '') {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(row.birthDate)) {
      errors.push('Invalid birth date format (must be YYYY-MM-DD)');
    }
  }

  if (row.country && row.country.trim() !== '') {
    const countryRegex = /^[A-Z]{2}$/i;
    if (!countryRegex.test(row.country)) {
      errors.push('Invalid country code (must be 2-letter ISO code)');
    }
  }

  return errors;
};

/**
 * Validate all rows and return validation results
 */
export const validateAllRows = (rows: UserImportRow[]): Map<number, string[]> => {
  const validationErrors = new Map<number, string[]>();

  rows.forEach((row, index) => {
    const errors = validateUserRow(row);
    if (errors.length > 0) {
      validationErrors.set(index, errors);
    }
  });

  return validationErrors;
};

/**
 * Read file as text
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};
